import API from 'packages/mybridgev4/store/common/api';
import { APIEndPoints, GlobalAPIEndpoints } from 'packages/mybridgev4/store/common/endPoint';

/**
 * 1. Create Upload Session
 * POST /api/v1/uploads/
 */
export const createUploadSessionApi = async ({
  upload_source,
  filename,
  mime_type,
  total_size,
  chunk_size,
  checksum = '',
}) => {
  try {
    const obj = {
      url: GlobalAPIEndpoints.UploadSession,
      method: 'POST',
      body: JSON.stringify({
        upload_source, // "chat" | "post" | "disk"
        filename,
        mime_type,
        total_size,
        chunk_size,
        checksum,
      }),
      throwErrorHandler: true,
    };
    const response = await API(obj);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to create upload session');
  }
};

/**
 * 2. Generate pre-signed Upload URLs for given part numbers
 * POST /api/v1/uploads/{upload_id}/parts/
 */
export const generatePartUrlsApi = async ({ uploadId, partNumbers }) => {
  try {
    const obj = {
      url: GlobalAPIEndpoints.UploadParts(uploadId),
      // method: 'POST',
      method: 'GET',
      // body: JSON.stringify({
      //   part_numbers: partNumbers, // array of part numbers like [1, 2, 3]
      // }),
      throwErrorHandler: true,
    };
    const response = await API(obj);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to generate part upload URLs');
  }
};

/**
 * 3. Upload a single part chunk via binary PUT request to presigned URL
 * Using XHR to support upload progress events, abort signal, and ETag header extraction.
 */
export const uploadPartChunkApi = ({
  url,
  chunk,
  partNumber,
  onProgress,
  signal,
}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    const handleAbort = () => {
      xhr.abort();
      reject(new Error('Upload aborted'));
    };

    const cleanupSignal = () => {
      if (signal) {
        signal.removeEventListener('abort', handleAbort);
      }
    };

    if (signal) {
      if (signal.aborted) {
        handleAbort();
      } else {
        signal.addEventListener('abort', handleAbort);
      }
    }

    // Track upload progress
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress({
            partNumber,
            loaded: event.loaded,
            total: event.total,
          });
        }
      };
    }

    // Handle the asynchronous server response
    xhr.onload = () => {
      cleanupSignal();
      if (xhr.status >= 200 && xhr.status < 300) {
        // let etag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag') || '';
        let etag = '';
        try {
          etag = xhr.getResponseHeader('Etag') || xhr.getResponseHeader('etag') || '';
        } catch (e) {
          etag = '';
        }
        // Clean double quotes around ETag if returned by S3
        if (etag) {
          etag = etag.replace(/^"|"$/g, '');
        }
        resolve({
          part_number: partNumber,
          e_tag: etag,
        });
      } else {
        reject(new Error(`Part ${partNumber} upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      cleanupSignal();
      reject(new Error(`Network error during part ${partNumber} upload`));
    };

    xhr.onabort = () => {
      cleanupSignal();
      reject(new Error(`Part ${partNumber} upload cancelled`));
    };

    // Send raw binary blob chunk
    xhr.send(chunk);
  });
};

/**
 * 4. Complete Upload Session
 * POST /api/v1/uploads/{upload_id}/complete/
 * Parts payload: [{ part_number: 1, e_tag: "..." }, { part_number: 2, e_tag: "..." }, ...]
 */
export const completeUploadSessionApi = async ({ uploadId, parts }) => {
  try {
    // Sort parts in ascending part_number order
    const sortedParts = [...parts].sort((a, b) => a.part_number - b.part_number);

    const obj = {
      url: GlobalAPIEndpoints.UploadComplete(uploadId),
      method: 'POST',
      body: JSON.stringify({
        parts: sortedParts,
      }),
      throwErrorHandler: true,
    };
    const response = await API(obj);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to complete upload session');
  }
};
